import React, { Component, ErrorInfo, ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleLogout = async () => {
    try {
      await signOut(auth);
      // Clear any cached data
      localStorage.clear();
      sessionStorage.clear();
      // Reload the page to reset the app state
      window.location.href = '/';
    } catch (e) {
      console.error('Error signing out:', e);
      // Force reload anyway
      window.location.href = '/';
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-white dark:bg-zinc-900">
          <div className="max-w-md p-8 mx-4 text-center bg-white rounded-lg shadow-lg dark:bg-zinc-800">
            <div className="flex justify-center mb-4">
              <svg
                className="w-16 h-16 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h1 className="mb-2 text-xl font-semibold text-zinc-900 dark:text-white">
              Something went wrong
            </h1>
            <p className="mb-6 text-sm text-zinc-600 dark:text-zinc-400">
              An unexpected error occurred. You can try reloading the page or sign out to reset your session.
            </p>
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-xs cursor-pointer text-zinc-500 dark:text-zinc-400">
                  Error details
                </summary>
                <pre className="p-2 mt-2 overflow-auto text-xs rounded bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300 max-h-32">
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div className="flex flex-col gap-3">
              <button
                onClick={this.handleReload}
                className="w-full px-4 py-2 text-sm font-medium text-white transition-colors bg-indigo-600 rounded-lg hover:bg-indigo-700"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleLogout}
                className="w-full px-4 py-2 text-sm font-medium transition-colors border rounded-lg text-zinc-700 dark:text-zinc-300 border-zinc-300 dark:border-zinc-600 hover:bg-zinc-50 dark:hover:bg-zinc-700"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
